import { t } from '@lingui/core/macro';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, useRouteContext } from '@tanstack/react-router';
import { useEffect } from 'react';
import { z } from 'zod';

import { notify } from '@/components/base/notifications/notifications';

import {
  SettingsSection,
  SettingsSectionSkeleton,
} from './-components/settings-section';
import { ZoomConnectionPanel } from './-components/zoom-connection-panel';
import { accountSettingsQueryOptions } from './-utils/account-settings-query';
import { SETTINGS_HELP } from './-utils/help-articles';

/**
 * The legacy Zoom OAuth callback appends `zoomConnected=true|false`. The router
 * parses search values as JSON, so it can arrive as a boolean or a string.
 */
const zoomSearchSchema = z.object({
  zoomConnected: z
    .union([z.boolean(), z.enum(['true', 'false'])])
    .transform((value) => value === true || value === 'true')
    .optional()
    .catch(undefined),
});

export const Route = createFileRoute(
  '/researchers/_authenticated/settings/zoom',
)({
  validateSearch: zoomSearchSchema,
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(
      accountSettingsQueryOptions(context.user.id),
    ),
  pendingComponent: SettingsSectionSkeleton,
  component: ZoomPage,
  head: () => ({ meta: [{ title: t`Zoom` }] }),
});

function ZoomPage() {
  const { user } = Route.useRouteContext();
  const { env } = useRouteContext({ from: '__root__' });
  const { zoomConnected } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { data: settings } = useSuspenseQuery(
    accountSettingsQueryOptions(user.id),
  );

  useEffect(() => {
    if (zoomConnected === undefined) return;
    notify(
      zoomConnected
        ? { title: t`Zoom account connected`, color: 'success' }
        : { title: t`Could not connect your Zoom account`, color: 'error' },
    );
    void navigate({ search: {}, replace: true });
  }, [zoomConnected, navigate]);

  return (
    <SettingsSection
      helpUrl={SETTINGS_HELP.zoom}
      title={t`Zoom`}
      description={t`Host moderated sessions on Zoom.`}
    >
      <ZoomConnectionPanel
        userId={user.id}
        websiteUrl={env.WEBSITE_URL}
        zoomAccountEmail={settings.zoomAccountEmail}
      />
    </SettingsSection>
  );
}
